"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { ChevronRight, Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem as WordPressMenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function RecursiveDesktopSubmenu({
  items
}: {
  items: WordPressMenuItem[];
}) {
  return <ul className="grid min-w-[200px] gap-1 p-2">
      {items.map((item, i) => <li key={item.id || `sub-${i}`} data-index={i}>
          {item.children && item.children.length > 0 ? <div className="space-y-1">
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="block rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                  {item.label}
                </Link>
              </NavigationMenuLink>
              <div className="pl-3">
                <RecursiveDesktopSubmenu items={item.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="block rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}
function DesktopNavigationMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-20 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id || `nav-${i}`} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="h-9 bg-transparent px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="border-border bg-popover text-popover-foreground shadow-xl">
                  <RecursiveDesktopSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-foreground/90 transition-colors hover:bg-accent hover:text-accent-foreground">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function RecursiveMobileList({
  items,
  onNavigate
}: {
  items: WordPressMenuItem[];
  onNavigate: () => void;
}) {
  return <ul className="space-y-1">
      {items.map((item, i) => <li key={item.id || `mob-${i}`} data-index={i}>
          {item.children && item.children.length > 0 ? <div className="space-y-1">
              <Link to={item.href || "#"} onClick={onNavigate} className="flex items-center justify-between rounded-md px-3 py-2.5 text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground">
                <span>{item.label}</span>
                <ChevronRight className="size-4 text-muted-foreground" />
              </Link>
              <div className="border-l border-border/50 pl-3">
                <RecursiveMobileList items={item.children} onNavigate={onNavigate} />
              </div>
            </div> : <Link to={item.href || "#"} onClick={onNavigate} className="flex items-center justify-between rounded-md px-3 py-2.5 text-base font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              <span>{item.label}</span>
            </Link>}
        </li>)}
    </ul>;
}
function MobileNavigationList({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 py-4">
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <div className="py-2">
      <RecursiveMobileList items={menuItems} onNavigate={onNavigate} />
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="dark" className={`sticky top-0 z-50 w-full transition-[background-color,border-color,box-shadow] duration-300 dark bg-background text-foreground ${isScrolled ? "border-b border-border/80 bg-background/95 shadow-md backdrop-blur-md" : "border-b border-border/30 bg-background/90 backdrop-blur-xs"}`}>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
        <div className="flex items-center">
          <Link to="/" className="inline-flex items-center transition-opacity hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring" aria-label="SANTA LIMO Homepage">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex lg:items-center lg:justify-center">
          <MenuProvider menu_id="16">
            <DesktopNavigationMenu />
          </MenuProvider>
        </div>

        <div className="hidden items-center gap-3 sm:flex">
          <a href="tel:3125550100" className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
            <Phone className="size-3.5 text-primary" />
            <span>(312) 555-0100</span>
          </a>

          <Button asChild size="default" className="font-semibold shadow-xs">
            <Link to="/OrderPage">Get a quote</Link>
          </Button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <Button asChild size="sm" className="sm:hidden font-semibold">
            <Link to="/OrderPage">Quote</Link>
          </Button>

          <SafeHydrate><Drawer open={isMobileOpen} onOpenChange={setIsMobileOpen} direction="right">
            <DrawerTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Open main menu" className="border-border/60 text-foreground hover:bg-muted">
                <Menu className="size-5" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="dark bg-background text-foreground border-border p-6">
              <DrawerHeader className="p-0 pb-4 text-left border-b border-border/40">
                <DrawerTitle className="text-base font-bold text-foreground">
                  Navigation
                </DrawerTitle>
              </DrawerHeader>

              <div className="mt-4 flex flex-1 flex-col justify-between overflow-y-auto">
                <MenuProvider menu_id="16">
                  <MobileNavigationList onNavigate={() => setIsMobileOpen(false)} />
                </MenuProvider>

                <div className="mt-8 space-y-4 border-t border-border/40 pt-6">
                  <a href="tel:3125550100" className="flex items-center gap-3 rounded-md bg-card p-3 text-sm font-medium text-foreground transition-colors hover:bg-muted">
                    <div className="flex size-8 items-center justify-center rounded-md bg-accent text-accent-foreground">
                      <Phone className="size-4" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">
                        24/7 Dispatch Line
                      </div>
                      <div className="font-semibold">(312) 555-0100</div>
                    </div>
                  </a>

                  <DrawerClose asChild>
                    <Button asChild className="w-full font-semibold">
                      <Link to="/OrderPage" onClick={() => setIsMobileOpen(false)}>
                        Get a quote
                      </Link>
                    </Button>
                  </DrawerClose>
                </div>
              </div>
            </DrawerContent>
          </Drawer></SafeHydrate>
        </div>
      </div>
    </section>;
}